import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'ada_action_whitelist'

export const useActionWhitelist = () => {
  // Get whitelisted actions from localStorage
  const getWhitelistedActions = useCallback((): string[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (!stored) return []
      return JSON.parse(stored) as string[]
    } catch (error) {
      console.error('Error reading action whitelist from localStorage:', error)
      return []
    }
  }, [])

  const [whitelistedActions, setWhitelistedActions] = useState<string[]>(() => getWhitelistedActions())

  useEffect(() => {
    const updateWhitelist = () => {
      setWhitelistedActions(getWhitelistedActions())
    }

    updateWhitelist()

    window.addEventListener('storage', updateWhitelist)

    return () => {
      window.removeEventListener('storage', updateWhitelist)
    }
  }, [getWhitelistedActions])

  // Check if an action is whitelisted
  const isActionWhitelisted = useCallback(
    (actionType: string): boolean => {
      return whitelistedActions.includes(actionType)
    },
    [whitelistedActions]
  )

  // Add an action to the whitelist
  const addToWhitelist = useCallback(
    (actionType: string) => {
      try {
        if (!whitelistedActions.includes(actionType)) {
          const updated = [...whitelistedActions, actionType]
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
          setWhitelistedActions(updated)
        }
      } catch (error) {
        console.error('Error adding action to whitelist:', error)
      }
    },
    [whitelistedActions]
  )

  // Remove an action from the whitelist
  const removeFromWhitelist = useCallback(
    (actionType: string) => {
      try {
        const updated = whitelistedActions.filter((action) => action !== actionType)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
        setWhitelistedActions(updated)
      } catch (error) {
        console.error('Error removing action from whitelist:', error)
      }
    },
    [whitelistedActions]
  )

  // Toggle an action's whitelist status
  const toggleWhitelist = useCallback(
    (actionType: string) => {
      if (isActionWhitelisted(actionType)) {
        removeFromWhitelist(actionType)
      } else {
        addToWhitelist(actionType)
      }
    },
    [isActionWhitelisted, addToWhitelist, removeFromWhitelist]
  )

  return {
    isActionWhitelisted,
    addToWhitelist,
    removeFromWhitelist,
    toggleWhitelist,
    whitelistedActions,
  }
}
